import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { BadgeCheck, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDE_IMG = "/images/piano-lesson.webp";

const NIVEAUX = ["Débutant", "Intermédiaire", "Avancé"];
const SOURCES = ["Réseaux sociaux", "Bouche à oreille", "Église", "Site web du CMC", "Autre"];

const EMPTY = { prenom: "", nom: "", email: "", telephone: "", niveau_musical: "", source: "" };

const inputCls =
  "bg-white border-[#E8DCD0] focus-visible:ring-[#D95D39] rounded-lg h-12";

export default function Registration() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.niveau_musical || !form.source) {
      toast.error("Merci de compléter tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/inscriptions`, form);
      setDone(true);
      toast.success(
        data.email_sent
          ? "Inscription confirmée ! Un email de confirmation vous a été envoyé."
          : "Inscription confirmée !"
      );
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="inscription" className="bg-[#2C1E16] py-28 grain relative" data-testid="registration-section">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -32 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5 flex flex-col justify-between gap-10"
        >
          <div>
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-[#F0A202] mb-4">
              Inscription gratuite
            </p>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl text-[#FFF4ED]">
              Réservez votre <span className="italic text-[#F0A202]">place</span>
            </h2>
            <p className="mt-5 text-[#D4BAA3] text-base leading-relaxed max-w-md">
              Les places sont limitées. Laissez-nous vos coordonnées et recevez immédiatement
              une confirmation par email.
            </p>
          </div>
          <div className="rounded-xl overflow-hidden border border-[#FFF4ED]/10 shadow-[0_8px_30px_rgb(217,93,57,0.15)]">
            <img src={SIDE_IMG} alt="Cours de piano gospel au conservatoire" className="w-full max-h-[520px] object-contain bg-[#1A1512]" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-7"
        >
          {done ? (
            <div
              data-testid="registration-success"
              className="h-full min-h-[420px] bg-[#FDFBF7] rounded-xl p-10 flex flex-col items-center justify-center text-center"
            >
              <BadgeCheck className="w-14 h-14 text-[#D95D39]" />
              <h3 className="mt-6 font-display font-black text-3xl text-[#2C1E16] tracking-tight">
                À très vite !
              </h3>
              <p className="mt-3 text-[#5C4A3D] max-w-sm">
                Votre inscription est bien enregistrée. Rendez-vous le samedi 10 octobre à 19h,
                81 Tollaan à Woluwe-Saint-Lambert.
              </p>
              <button
                data-testid="register-another-button"
                onClick={() => setDone(false)}
                className="mt-8 rounded-full border border-[#2C1E16] px-6 py-2.5 text-sm font-semibold text-[#2C1E16] transition-[transform,background-color,color] duration-300 hover:scale-105 hover:bg-[#2C1E16] hover:text-[#FDFBF7]"
              >
                Inscrire une autre personne
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="registration-form"
              className="bg-[#FDFBF7] rounded-xl p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.25)]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="prenom" className="text-[#2C1E16] font-semibold">Prénom *</Label>
                  <Input id="prenom" data-testid="prenom-input" required value={form.prenom} onChange={set("prenom")} className={`mt-2 ${inputCls}`} placeholder="Marie" />
                </div>
                <div>
                  <Label htmlFor="nom" className="text-[#2C1E16] font-semibold">Nom *</Label>
                  <Input id="nom" data-testid="nom-input" required value={form.nom} onChange={set("nom")} className={`mt-2 ${inputCls}`} placeholder="Dupont" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#2C1E16] font-semibold">Adresse email *</Label>
                  <Input id="email" type="email" data-testid="email-input" required value={form.email} onChange={set("email")} className={`mt-2 ${inputCls}`} placeholder="marie@exemple.be" />
                </div>
                <div>
                  <Label htmlFor="telephone" className="text-[#2C1E16] font-semibold">Numéro de téléphone *</Label>
                  <Input id="telephone" type="tel" data-testid="telephone-input" required minLength={6} value={form.telephone} onChange={set("telephone")} className={`mt-2 ${inputCls}`} placeholder="+32 470 12 34 56" />
                </div>
                <div>
                  <Label className="text-[#2C1E16] font-semibold">Niveau au piano *</Label>
                  <Select value={form.niveau_musical} onValueChange={(v) => setForm((f) => ({ ...f, niveau_musical: v }))}>
                    <SelectTrigger data-testid="niveau-select" className={`mt-2 ${inputCls}`}>
                      <SelectValue placeholder="Choisissez votre niveau" />
                    </SelectTrigger>
                    <SelectContent>
                      {NIVEAUX.map((n) => (
                        <SelectItem key={n} value={n} data-testid={`niveau-option-${n.toLowerCase()}`}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#2C1E16] font-semibold">Comment avez-vous entendu parler de la masterclass ? *</Label>
                  <Select value={form.source} onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}>
                    <SelectTrigger data-testid="source-select" className={`mt-2 ${inputCls}`}>
                      <SelectValue placeholder="Sélectionnez une réponse" />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`source-option-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-registration-button"
                className="mt-8 w-full rounded-full bg-[#D95D39] text-[#FDFBF7] py-4 text-base font-bold tracking-wide transition-[transform,background-color] duration-300 hover:scale-[1.02] hover:bg-[#7A2021] disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                {loading ? "Envoi en cours…" : "Je confirme mon inscription gratuite"}
              </button>
              <p className="mt-4 text-xs text-[#5C4A3D] text-center">
                Organisé par CMC-Belgique — Conservatoire de la Musique Chrétienne. Vos données ne
                servent qu'à gérer votre inscription.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
